import { useIntl } from 'react-intl'
import useBedbankRates, { useBedbankRatesForIndividualRooms } from 'hooks/useBedbankRates'
import useBedbankOfferSell from 'hooks/useBedbankOfferSell'
import getBedbankPromoExtraValue, { getDisplayTotalPrice } from 'lib/hotels/bedbankUtils'
import type { AggregatedMultiRoomPrices } from 'lib/hotels/bedbankUtils'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import OfferPageStickyFooter from './OfferPageStickyFooter'
import WalledContent from 'components/Common/WalledContent'
import Group from 'components/utils/Group'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import { useOfferPrice } from 'hooks/Offers/useOfferPrice'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import { getHotelOfferDisplayedValue } from 'lib/offer/getOfferDisplayPricing'
import { useBedbankRoomRatePromotions } from 'hooks/OfferPage/useBedbankPromotions'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import useBedbankFlightPrice from 'hooks/OfferPage/useBedbankFlightPrice'
import { EmptyArray } from 'lib/array/arrayUtils'
import { getCanUseMultiRoomUI } from 'selectors/offerPage/bedbankOfferPageSelectors'
import { getBedbankMultiRoomPricingTotals } from 'selectors/offerPage/bedbankMultipleRoomBookingSelectors'

interface Props {
  canViewLuxPlusBenefits: boolean
  duration: number
  offer: App.BedbankOffer
  rooms?: Array<App.Occupants>
  checkInDate?: string
  checkOutDate?: string
  flightPrice?: number
  flightsEnabled: boolean
  onScrollTo?: () => void
  airport?: App.Airport
  multiRoomPrices?: AggregatedMultiRoomPrices
  isMultiRoomIndividualMode: boolean
}

function BedbankOfferPageStickyFooter(props: Props) {
  const {
    offer,
    rooms = EmptyArray,
    checkInDate,
    checkOutDate,
    duration,
    flightsEnabled,
    onScrollTo,
    canViewLuxPlusBenefits,
    airport,
    multiRoomPrices,
    isMultiRoomIndividualMode,
  }: Props = props

  const intl = useIntl()

  const flightPrice = useBedbankFlightPrice(offer, {
    rooms,
    startDate: checkInDate,
    endDate: checkOutDate,
    originAirportCode: airport?.code,
  })

  const [{
    hotelOnlyRates,
    hotelBundleRates,
  }, fetching] = useBedbankRates(offer.id, rooms, checkInDate, checkOutDate)

  const [, individualRatesFetching] = useBedbankRatesForIndividualRooms(
    isMultiRoomIndividualMode ? offer.id : undefined,
    rooms,
    checkInDate,
    checkOutDate,
    isMultiRoomIndividualMode,
  )

  const useMultiRoomPrices = !!multiRoomPrices
  const isFetching = isMultiRoomIndividualMode ? individualRatesFetching : fetching

  const rate = hotelOnlyRates[0]
  const bundleRate = hotelBundleRates[0] || rate

  const sell = useBedbankOfferSell(offer, duration, rooms)

  const hotelPrice = useMultiRoomPrices ? multiRoomPrices.hotelPrice :
    rate ? rate.totals.inclusive : sell?.price ?? 0
  const hotelMemberPrice = useMultiRoomPrices ? multiRoomPrices.hotelMemberPrice :
    rate ? rate.totals.memberInclusive : 0
  const bundlePrice = useMultiRoomPrices ? multiRoomPrices.bundlePrice :
    bundleRate?.totals.inclusive || hotelPrice

  const hotelNights = multiRoomPrices?.hotelNights ?? rate?.nights ?? sell?.los

  const propertyFees = useMultiRoomPrices ? multiRoomPrices.propertyFees :
    rate?.totals?.propertyFees ?? sell?.propertyFees ?? 0
  const bundlePropertyFees = useMultiRoomPrices ? multiRoomPrices.bundlePropertyFees :
    bundleRate?.totals.propertyFees || propertyFees

  let hotelValue: undefined | number
  let bundleValue: undefined | number

  let hotelDiscount: number
  let hotelMemberDiscount: number = 0

  const promo = useBedbankRoomRatePromotions(offer.promotions, checkInDate, checkOutDate)
  if (useMultiRoomPrices) {
    hotelValue = multiRoomPrices.hotelValue
    bundleValue = multiRoomPrices.bundleValue
    hotelDiscount = 0
  } else if (rate) {
    const pkg = rate.room
    const isPromoEnabledRate = !!promo[rate.promotionId ?? '']

    if (isPromoEnabledRate) {
      const inclusionsAmount = pkg.sell ? getBedbankPromoExtraValue({
        price: pkg.sell.price,
        value: pkg.sell.value,
        minLos: pkg.sell.los,
        duration,
      }) : 0
      hotelValue = inclusionsAmount + rate.totals.inclusive
      bundleValue = inclusionsAmount + (bundleRate?.totals.inclusive || hotelValue)
      hotelDiscount = getValueOffPercent(hotelValue, inclusionsAmount)
    } else {
      hotelValue = rate.value
      bundleValue = bundleRate?.value || hotelValue
      hotelDiscount = rate.discount
      hotelMemberDiscount = rate.memberDiscount
    }
  } else {
    hotelValue = sell?.value
    bundleValue = sell?.value
    hotelDiscount = sell?.discountPercentage ?? 0
  }

  const datesSelected = !!(checkInDate && checkOutDate)
  const promoteAsBundle = flightsEnabled && !!flightPrice && offer.promoteAsBundle

  const totalPrices = getDisplayTotalPrice({
    hotelPrice: hotelPrice + propertyFees,
    hotelMemberPrice: hotelMemberPrice ? hotelMemberPrice + propertyFees : 0,
    hotelValue,
    bundlePrice: bundlePrice + bundlePropertyFees,
    bundleValue,
    hotelDiscount,
    hotelMemberDiscount,
    flightPrice,
    checkIn: checkInDate,
    rooms,
    displayPricingAsPerPerson: offer.displayPricingAsPerPerson,
  })

  const offerPrice = promoteAsBundle ? totalPrices.totals.flightBundlePrice : totalPrices.totals.price
  const offerMemberPrice = promoteAsBundle ? totalPrices.totals.flightBundleMemberPrice : totalPrices.totals.memberPrice
  const isSoldOut = !useMultiRoomPrices && !isMultiRoomIndividualMode && hotelOnlyRates.length === 0 && datesSelected

  const shownValue = promoteAsBundle ? bundleValue : hotelValue

  const {
    saleUnit,
    total: displayOfferPrice,
    memberTotal: displayMemberPrice,
  } = useOfferPrice({
    value: shownValue,
    displayPricingAsPerPerson: offer.displayPricingAsPerPerson,
    saleUnit: offer.displayPricingAsPerPerson ? 'person' : 'room',
    duration: hotelNights,
    total: offerPrice,
    memberTotal: offerMemberPrice,
  })

  const displayPrice = ((displayMemberPrice ?? 0) > 0 && canViewLuxPlusBenefits) ? displayMemberPrice : displayOfferPrice

  const { shouldShowValue, shouldShowDiscountPercent, highestDiscountPercent } = getHotelOfferDisplayedValue(false, displayPrice ?? 0, hotelValue ?? 0, true)

  const viewOptionsCopy = intl.formatMessage({
    id: 'BI2EjhefWB-sRHXh3Hb1',
    defaultMessage: 'View options',
  })

  const nightsWithFlightsFromLabel = intl.formatMessage(
    {
      id: 'P97JKMCHlggscHAfVOO7',
      defaultMessage: '{nights, plural, one {# night} other {# nights}} + flights from',
    },
    { nights: hotelNights },
  )
  const nightsFromLabel = intl.formatMessage(
    {
      id: 'RDd7sJSGpzPJv0NO8jb7',
      defaultMessage: '{nights, plural, one {# night} other {# nights}} from',
    },
    { nights: hotelNights },
  )

  if (isSoldOut) {
    return null
  }

  return (
    <OfferPageStickyFooter
      visible
      callToActionCopy={viewOptionsCopy}
      offer={offer}
      onCTAClick={onScrollTo}
      hidePricing={offer.hidePricing}
    >
      <WalledContent enforced={offer.walledGarden} hidePricing={offer.hidePricing}>
        {!isFetching && !!displayOfferPrice &&
          <Group direction="vertical">
            <PriceRowPriceCaption>
              {promoteAsBundle ? nightsWithFlightsFromLabel : nightsFromLabel}
            </PriceRowPriceCaption>
            <LuxPlusPriceGuard
              showMemberPrice={(displayMemberPrice ?? 0) > 0}
              fallbackPrice={<PriceRowPrice
                size="S"
                price={displayOfferPrice}
                saleUnit={saleUnit}
              />}
            >
              <PriceRowLuxPlusPrice
                size="S"
                memberPrice={displayMemberPrice ?? 0}
                saleUnit={saleUnit}
                logoPlacement="leading"
              />
            </LuxPlusPriceGuard>
            {shouldShowValue && shouldShowDiscountPercent && shownValue &&
              <PriceRowValueDiscountWithCaption
                size="M"
                originalValue={shownValue}
                discountPercentage={highestDiscountPercent}
              />}
          </Group>}
      </WalledContent>
    </OfferPageStickyFooter>
  )
}

type MappedProps = Pick<Props,
  'airport' |
  'duration' |
  'checkInDate' |
  'checkOutDate' |
  'offer' |
  'rooms' |
  'canViewLuxPlusBenefits' |
  'multiRoomPrices' |
  'isMultiRoomIndividualMode'
> & { open: boolean }

export default withOfferPageAndReduxState((offerState, state): MappedProps => {
  const isMultiRoomIndividualMode = getCanUseMultiRoomUI(offerState, state) && offerState.multiRoomMode === 'individual'
  return {
    airport: offerState.airport,
    duration: offerState.duration,
    checkInDate: offerState.checkInDate,
    checkOutDate: offerState.checkOutDate,
    offer: offerState.bedbankOffer!,
    rooms: offerState.rooms,
    open: !!offerState.activeFilter,
    canViewLuxPlusBenefits: getCanViewLuxPlusBenefits(state),
    isMultiRoomIndividualMode,
    multiRoomPrices: isMultiRoomIndividualMode ? getBedbankMultiRoomPricingTotals(offerState, state) : undefined,
  }
}, BedbankOfferPageStickyFooter, preventRenderWithOpenFilter)
